
import { useNavigate } from "react-router-dom";
import { useAIStaffData } from "./useAIStaffData";
import { useStaffProgress } from "./staff/useStaffProgress";
import { usePaymentStatus } from "./staff/usePaymentStatus";
import { useStaffSelection } from "./staff/useStaffSelection";
import { usePlanSelection } from "./staff/usePlanSelection";
import { useSubscriptionManagement } from "./staff/useSubscriptionManagement";
import { useStaffViewMode } from "./staff/useStaffViewMode";
import { useWorkflowActions } from "./staff/useWorkflowActions";
import { useEffect, useRef, useState } from "react";
import { useToast } from "@/hooks/use-toast";

export function useStaffManagement(specificStaffId?: string) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const initialFetchDone = useRef(false);
  const [isInitializing, setIsInitializing] = useState(true);
  
  // Custom hooks
  const { 
    aiStaff, 
    allAIStaff, 
    fetchAIStaff, 
    fetchAllAIStaff,
    isLoading: dataLoading
  } = useAIStaffData();
  
  const { selectedCandidate, setSelectedCandidate, handleSelectCandidate } = useStaffSelection();
  const { handleSelectPlan } = usePlanSelection();
  const { isSubmitting, handleCancelSubscription } = useSubscriptionManagement(specificStaffId);
  const { isViewingExistingStaff, staffId } = useStaffViewMode(specificStaffId);
  const { handleComplete } = useWorkflowActions();
  const {
    currentStage,
    progress,
    staffStages,
    handleNextStage,
    handlePreviousStage
  } = useStaffProgress();

  // Initialize payment status callback
  usePaymentStatus(() => {
    if (staffId) {
      fetchAIStaff(staffId);
    } else if (currentStage >= 2) {
      fetchAIStaff();
    }
  });

  // Add an effect to fetch staff data only if we don't already have it
  useEffect(() => {
    if (initialFetchDone.current) {
      return;
    }

    const fetchDataIfNeeded = async () => {
      try {
        setIsInitializing(true);
        if (staffId && isViewingExistingStaff) {
          console.log("StaffManagement - Initial fetch for specific staff with ID:", staffId);
          const result = await fetchAIStaff(staffId);
          if (!result) {
            toast({
              title: "Staff not found",
              description: "The requested staff member could not be found. Returning to dashboard.",
              variant: "destructive",
            });
            setTimeout(() => navigate("/app"), 2000);
          }
        } else if (isViewingExistingStaff && !staffId) {
          // Only if we're viewing existing staff but don't have a specific ID, fetch all
          console.log("StaffManagement - Initial fetch for all staff");
          await fetchAllAIStaff();
        }
        initialFetchDone.current = true;
      } catch (error) {
        console.error("Error in staff management data fetching:", error);
        toast({
          title: "Loading error",
          description: "Failed to load staff data. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsInitializing(false);
      }
    };

    fetchDataIfNeeded();
  }, [staffId, isViewingExistingStaff, fetchAIStaff, fetchAllAIStaff, toast, navigate]);

  // Additional effect for stage 2 (onboarding)
  useEffect(() => {
    if (currentStage === 2 && !initialFetchDone.current) {
      // When we reach the onboarding stage, try to fetch the AI staff
      // This helps ensure we have the latest AI staff data
      fetchAIStaff();
      initialFetchDone.current = true;
    }
  }, [currentStage, fetchAIStaff]);

  // Calculate the overall loading state
  const isLoading = dataLoading || isInitializing;

  return {
    currentStage,
    progress,
    isSubmitting,
    aiStaff,
    allAIStaff,
    selectedCandidate,
    staffStages,
    handleNextStage,
    handlePreviousStage,
    handleSelectCandidate,
    handleSelectPlan,
    handleComplete,
    handleCancelSubscription,
    isViewingExistingStaff,
    staffId,
    fetchAIStaff,
    isLoading
  };
}
